import { Component, computed, inject, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { map, merge, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyOwnershipService } from '../../../../../services/companies/company-ownership.service';
import { LutService } from '../../../../../services/lut.service';
import { StockCoefficient } from '../../../../../models/stockCoefficient';
import { StockCategory } from '../../../../../models/stockCategory';
import { BoardMemberOccupation } from '../../../../../models/boardMemberOccupation';
import {
  OwnershipResponseDto,
  OwnershipBoardMemberDto,
  OwnershipExecutiveMemberDto,
  OwnershipLargeShareholderDto,
  OwnershipUpsertRequest,
  OwnershipBoardMemberUpsertDto,
  OwnershipExecutiveMemberUpsertDto,
  OwnershipLargeShareholderUpsertDto,
} from '../../../../../models/api/companyOwnershipDto';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import {
  MEMBERS_DROPPED_MESSAGE,
  membersWereDropped,
  SHAREHOLDERS_DROPPED_MESSAGE,
  shareholdersWereDropped
} from '../../../../../models/api/yearlyResource';
import { selectTabYear, selectDefaultYear, selectCompanyName } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { AddShareholderDialogComponent, AddShareholderDialogResult } from './add-shareholder-dialog/add-shareholder-dialog.component';
import { EntityType } from '../../../../../models/enums/entityType.enum';
import { LargeShareholderService } from '../../../../../services/large-shareholder.service';
import { LargeShareholderCategory } from '../../../../../models/largeShareholderCategory';
import { CreateLargeShareholderRequest } from '../../../../../models/api/createLargeShareholderRequest';
import { CreateConnectionRequest } from '../../../../../models/api/createConnectionRequest';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { LutCodes } from '../../../../../utilities/lutCodes';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-ownership-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    RouterLink,
    ReadOnlyTabDirective,
  ],
  templateUrl: './ownership-tab.component.html',
  styleUrl: './ownership-tab.component.scss'
})
export class OwnershipTabComponent implements OnInit, OnDestroy, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private ownershipService = inject(CompanyOwnershipService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private lutService = inject(LutService);
  private dialog = inject(MatDialog);
  private largeShareholderService = inject(LargeShareholderService);
  private companyKeywordService = inject(CompanyKeywordService);
  private currentUserState = inject(CurrentUserState);
  companyName = this.store.selectSignal(selectCompanyName);
  private allCategories: LargeShareholderCategory[] = [];

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Ownership));

  stockCoefficients: StockCoefficient[] = [];
  stockCategories: StockCategory[] = [];
  occupations: BoardMemberOccupation[] = [];
  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  otherMeasurement: string = '';
  otherData: (OwnershipResponseDto & { boardMembers: OwnershipBoardMemberDto[]; executiveMembers: OwnershipExecutiveMemberDto[] }) | null = null;
  companyId: string | null = null;
  showYearWarning = false;
  isLargeShareholdersCarriedForward = false;
  isStockClassConfigCarriedForward = false;
  isOwnershipDetailsCarriedForward = false;
  showKeywords = true;
  companyBoardType: number = 0;
  private destroy$ = new Subject<void>();
  private rowChanges$ = new Subject<void>();
  /** Surrogate ids of existing ownership rows the user removed (sent to the API for deletion). */
  private deletedOwnershipRowIds: string[] = [];
  /** Client temp ids of rows added this session (no DB id yet — inserted on save). */
  private newRowIds: string[] = [];
  /** True while a load is patching the form, so the recalculations it triggers aren't taken for user edits. */
  private isPopulating = false;

  /** Board members table (Unitary: "Board of Directors", Dual: "Supervisory Board" with boardType=1) */
  boardDataSource = new MatTableDataSource<OwnershipBoardMemberDto>([]);
  /** Management board (Dual only, boardType=2) */
  managementDataSource = new MatTableDataSource<OwnershipBoardMemberDto>([]);
  /** Executive members table */
  executiveDataSource = new MatTableDataSource<OwnershipExecutiveMemberDto>([]);
  /** Large shareholders table */
  shareholderDataSource = new MatTableDataSource<OwnershipLargeShareholderDto>([]);

  @ViewChild('boardSort') boardSort!: MatSort;
  @ViewChild('managementSort') managementSort!: MatSort;
  @ViewChild('executiveSort') executiveSort!: MatSort;
  @ViewChild('shareholderSort') shareholderSort!: MatSort;

  boardDisplayedColumns: string[] = [
    'index', 'name',
    'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares',
    'votingRightPercentage', 'votingRights'
  ];

  executiveDisplayedColumns: string[] = [
    'index', 'name',
    'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares',
    'votingRightPercentage', 'votingRights'
  ];

  shareholderDisplayedColumns: string[] = [
    'index', 'name',
    'firstClassSharesPercentage', 'firstClassShares',
    'secondClassSharesPercentage', 'secondClassShares',
    'thirdClassSharesPercentage', 'thirdClassShares',
    'votingRightPercentage', 'votingRights',
    'category', 'subCategory', 'delete'
  ];

  form: FormGroup = this.fb.group({
    // Stock class configuration
    firstClassStockCoefficientId: [null],
    firstClassStockCategoryId: [null],
    firstClassStockType: [''],
    firstClassTotalShares: [null],
    firstClassCapitalSharePercentage: [null],
    firstClassTotalVotingRightsPercentage: [null],
    firstClassTotalVotingRights: [null],
    secondClassStockCoefficientId: [null],
    secondClassStockCategoryId: [null],
    secondClassStockType: [''],
    secondClassTotalShares: [null],
    secondClassCapitalSharePercentage: [null],
    secondClassTotalVotingRightsPercentage: [null],
    secondClassTotalVotingRights: [null],
    thirdClassStockCoefficientId: [null],
    thirdClassStockCategoryId: [null],
    thirdClassStockType: [''],
    thirdClassTotalShares: [null],
    thirdClassCapitalSharePercentage: [null],
    thirdClassTotalVotingRightsPercentage: [null],
    thirdClassTotalVotingRights: [null],
    // Member rows
    boardRows: this.fb.array([]),
    managementRows: this.fb.array([]),
    executiveRows: this.fb.array([]),
    shareholderRows: this.fb.array([]),
    // Comments
    comments: [''],
    keywords: this.fb.group({
      boardShares: [''],
      largestShareholder: [''],
      totalShares: ['']
    })
  });

  get boardRowsArray(): FormArray {
    return this.form.get('boardRows') as FormArray;
  }

  get managementRowsArray(): FormArray {
    return this.form.get('managementRows') as FormArray;
  }

  get executiveRowsArray(): FormArray {
    return this.form.get('executiveRows') as FormArray;
  }

  get shareholderRowsArray(): FormArray {
    return this.form.get('shareholderRows') as FormArray;
  }

  get hasChanges(): boolean {
    return this.form.dirty || this.deletedOwnershipRowIds.length > 0 || this.newRowIds.length > 0;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  /** Total capital shares across all classes */
  get capitalShareTotal(): string {
    const total = (this.form.get('firstClassTotalShares')?.value || 0)
      + (this.form.get('secondClassTotalShares')?.value || 0)
      + (this.form.get('thirdClassTotalShares')?.value || 0);
    return total ? total.toLocaleString('de-DE') : '';
  }

  /** Total voting rights across all classes */
  get votingRightsTotal(): string {
    const total = (this.form.get('firstClassTotalVotingRights')?.value || 0)
      + (this.form.get('secondClassTotalVotingRights')?.value || 0)
      + (this.form.get('thirdClassTotalVotingRights')?.value || 0);
    return total ? total.toLocaleString('de-DE') : '';
  }

  /**
   * Free float = 100 - sum of member percentages per class. Shown whenever the class has total
   * shares configured (free float = 100 when no member holds any) or someone holds a percentage;
   * blank only when the class is entirely unused.
   */
  freeFloatValue(controlName: string): number | null {
    const totalPct = this.sumPercentage(controlName);
    const totalShares = this.form.get(controlName.replace('SharesPercentage', 'TotalShares'))?.value || 0;
    return (totalShares || totalPct) ? 100 - totalPct : null;
  }

  /** Free float of the comparison measurement, computed from otherData the same way as the live one */
  otherFreeFloatValue(field: string): number | null {
    if (!this.otherData) return null;
    const totalPct = [
      ...(this.otherData.boardMembers ?? []),
      ...(this.otherData.executiveMembers ?? []),
      ...(this.otherData.largeShareholders ?? [])
    ].reduce((sum, m: any) => sum + (m[field] || 0), 0);
    const totalShares = (this.otherData as any)[field.replace('SharesPercentage', 'TotalShares')] || 0;
    return (totalShares || totalPct) ? 100 - totalPct : null;
  }

  formatFreeFloat(value: number | null): string {
    return value === null ? '' : value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  get freeFloatFirstClass(): string {
    return this.formatFreeFloat(this.freeFloatValue('firstClassSharesPercentage'));
  }

  get freeFloatSecondClass(): string {
    return this.formatFreeFloat(this.freeFloatValue('secondClassSharesPercentage'));
  }

  get freeFloatThirdClass(): string {
    return this.formatFreeFloat(this.freeFloatValue('thirdClassSharesPercentage'));
  }

  /** A negative free float means shareholder percentages exceed 100% — flagged in red */
  get isFreeFloatFirstClassNegative(): boolean {
    const value = this.freeFloatValue('firstClassSharesPercentage');
    return value !== null && value < 0;
  }

  get isFreeFloatSecondClassNegative(): boolean {
    const value = this.freeFloatValue('secondClassSharesPercentage');
    return value !== null && value < 0;
  }

  get isFreeFloatThirdClassNegative(): boolean {
    const value = this.freeFloatValue('thirdClassSharesPercentage');
    return value !== null && value < 0;
  }

  toggleRowHighlight(row: any): void {
    row.highlighted = !row.highlighted;
  }

  onBoardSort(sort: Sort): void {
    this.sortBoardTable(sort, this.boardDataSource, this.boardRowsArray, 'boardRows');
  }

  onManagementSort(sort: Sort): void {
    this.sortBoardTable(sort, this.managementDataSource, this.managementRowsArray, 'managementRows');
  }

  onExecutiveSort(sort: Sort): void {
    this.sortExecutiveTable(sort);
  }

  onShareholderSort(sort: Sort): void {
    this.sortShareholderTable(sort);
  }

  /**
   * The table's current sort, taken from its MatSort header so that rebuilding the
   * rows after a reload (e.g. a save) keeps the column the user picked instead of
   * snapping the data back to name/asc — which left the sort arrow pointing at one
   * column while the rows were ordered by another. Falls back to name/asc before the
   * header has rendered (first load).
   */
  private currentSort(sort: MatSort | undefined): Sort {
    return sort?.active && sort.direction
      ? { active: sort.active, direction: sort.direction }
      : { active: 'name', direction: 'asc' };
  }

  private sumPercentage(field: string): number {
    const arrays = [this.boardRowsArray, this.managementRowsArray, this.executiveRowsArray, this.shareholderRowsArray];
    return arrays.reduce((total, arr) =>
      total + arr.controls.reduce((sum, ctrl) => sum + (ctrl.get(field)?.value || 0), 0), 0
    );
  }

  isDifferent(current: number | null | undefined, other: number | null | undefined): boolean {
    return (current || 0) !== (other || 0);
  }

  private normalizeStockType(value: string | null | undefined): string {
    if (!value) return '';
    const lower = value.toLowerCase();
    if (lower === 'listed') return 'Listed';
    if (lower === 'unlisted') return 'Unlisted';
    return value;
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('ownership'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'ownership', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'ownership', year: this.selectedYear }));
    }

    this.lutService.getStockCoefficients().subscribe(data => {
      this.stockCoefficients = data.sort((a, b) => (b.priority === a.priority ? 0 : b.priority ? 1 : -1) || a.value - b.value);
    });

    this.lutService.getStockCategories().subscribe(data => {
      this.stockCategories = data.sort((a, b) => a.name.localeCompare(b.name));
    });

    this.lutService.getBoardMemberOccupations().subscribe(data => {
      this.occupations = data;
    });

    this.lutService.getLargeShareholderCategories().subscribe(data => {
      this.allCategories = data;
    });

    this.patchKeywords();
    this.subscribeToShareChanges();
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const boardShares = this.keywords.find(k => k.type === KeywordType.OwnershipBoardShares);
    const largestShareholder = this.keywords.find(k => k.type === KeywordType.OwnershipLargestShareholder);
    const totalShares = this.keywords.find(k => k.type === KeywordType.OwnershipTotalShares);
    this.form.get('keywords')!.patchValue({
      boardShares: boardShares?.value ?? '',
      largestShareholder: largestShareholder?.value ?? '',
      totalShares: totalShares?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasOwnershipChanges = this.hasChanges && !hasKeywordChanges;

    if (hasOwnershipChanges && hasKeywordChanges) return 'ownership data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'ownership data';
  }

  ngOnDestroy(): void {
    this.rowChanges$.next();
    this.rowChanges$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onSave(): void {
    if (!this.companyId) return;

    if (this.updateStockClassErrors(true)) {
      this.toast.error('Coefficient, Category and Type must be selected in Stock Classes for each share class with capital shares');
      return;
    }

    this.performSave();
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  /** Find a board member's other measurement counterpart by directorId */
  getOtherBoardMember(directorId: string): OwnershipBoardMemberDto | undefined {
    return this.otherData?.boardMembers?.find(m => m.directorId === directorId);
  }

  /** Find an executive member's other measurement counterpart by directorId */
  getOtherExecutiveMember(directorId: string): OwnershipExecutiveMemberDto | undefined {
    return this.otherData?.executiveMembers?.find(m => m.directorId === directorId);
  }

  /**
   * Find a large shareholder's other-measurement counterpart by position among same-shareholder
   * rows: the Nth occurrence of a shareholder in the current year pairs with the Nth occurrence
   * in the other year. Needed because the same shareholder can legitimately appear multiple
   * times in a single year.
   */
  getOtherShareholder(row: OwnershipLargeShareholderDto): OwnershipLargeShareholderDto | undefined {
    if (!this.otherData?.largeShareholders) return undefined;
    const lshId = row.largeShareholderId;
    const occurrenceIndex = this.shareholderDataSource.data
      .filter(r => r.largeShareholderId === lshId)
      .findIndex(r => r.id === row.id);
    if (occurrenceIndex === -1) return undefined;
    return this.otherData.largeShareholders
      .filter(o => o.largeShareholderId === lshId)[occurrenceIndex];
  }

  onInsertShareholder(): void {
    if (!this.companyId) return;

    const dialogRef = this.dialog.open(AddShareholderDialogComponent, {
      width: '900px',
      panelClass: 'connections-dialog-panel',
      data: { year: this.selectedYear }
    });

    dialogRef.afterClosed().subscribe((result: AddShareholderDialogResult | null) => {
      if (!result) return;

      if (result.action === 'created') {
        this.addShareholderToOwnership({
          ...result.item,
          categoryName: result.item.categoryName,
          subCategoryName: result.item.subCategoryName
        });
      } else if (result.action === 'connect') {
        const entityType = result.item.entityType;
        if (entityType === EntityType.LargeShareholder || entityType === EntityType.PyramidalShareholder) {
          this.addShareholderToOwnership({
            ...result.item,
            categoryName: this.getCategoryName(result.item.categoryId),
            subCategoryName: this.getCategoryName(result.item.subCategoryId)
          });
        } else {
          this.createLshAndAdd(result.item);
        }
      }
    });
  }

  private addShareholderToOwnership(item: {
    id: string; name: string; entityType: number;
    categoryName?: string | null; subCategoryName?: string | null;
  }): void {
    const tempRowId = crypto.randomUUID();
    const newShareholder: OwnershipLargeShareholderDto = {
      id: tempRowId,
      year: parseInt(this.selectedYear),
      companyId: this.companyId!,
      largeShareholderId: item.id,
      largeShareholderName: item.name,
      entityType: item.entityType,
      categoryName: item.categoryName ?? null,
      subCategoryName: item.subCategoryName ?? null,
      firstClassShares: 0,
      firstClassSharesPercentage: 0,
      secondClassShares: 0,
      secondClassSharesPercentage: 0,
      thirdClassShares: 0,
      thirdClassSharesPercentage: 0,
      votingRights: 0,
      votingRightPercentage: 0,
      isCarriedForward: false,
      createdDate: '',
      createdBy: '',
      updatedDate: '',
      updatedBy: ''
    };

    // Add to data source
    this.shareholderDataSource.data = [...this.shareholderDataSource.data, newShareholder];

    // Track as new addition (by its temp row id)
    this.newRowIds.push(tempRowId);

    // Add to form array
    const rowGroup = this.createShareholderRowGroup(newShareholder);
    this.shareholderRowsArray.push(rowGroup);

    this.onShareholderSort(this.currentSort(this.shareholderSort));
    this.subscribeToRowControls();
    this.form.markAsDirty();
    this.toast.success(`"${item.name}" added to the list`);
  }

  private getCategoryName(id?: string): string | null {
    if (!id) return null;
    return this.allCategories.find(c => c.id === id)?.name ?? null;
  }

  /**
   * For Director/Company/OpbCompany: create a new large shareholder via API, then add to local list.
   *
   * A director is a natural person, so the new shareholder is classified External Blockholders /
   * Individual; a company or OPB company becomes a Widely-Held Firm. Categories are matched on
   * their immutable code — the Individual row's name is stored as "Indivindual", so matching on
   * the name left the sub-category blank and the user had to set it by hand.
   */
  private createLshAndAdd(item: { id: string; name: string; entityType: number }): void {
    const isDirector = item.entityType === EntityType.Director;
    const categoryCode = isDirector
      ? LutCodes.shareholderCategory.externalBlockholders
      : LutCodes.shareholderCategory.widelyHeldFirm;
    const subcategoryCode = isDirector ? LutCodes.shareholderCategory.individual : null;

    const category = this.allCategories.find(c => c.parentId === null && c.code === categoryCode);
    if (!category) {
      this.toast.error(`Category "${categoryCode}" not found`);
      return;
    }

    const subcategory = subcategoryCode
      ? this.allCategories.find(c => c.parentId === category.id && c.code === subcategoryCode)
      : null;
    if (subcategoryCode && !subcategory) {
      this.toast.warning(`Sub-category "${subcategoryCode}" not found — the shareholder will be created without one`);
    }

    const request: CreateLargeShareholderRequest = {
      year: parseInt(this.selectedYear),
      name: item.name,
      address: '',
      website: '',
      email: '',
      countryId: '',
      categoryId: category.id,
      subCategoryId: subcategory?.id ?? '',
      notes: ''
    };

    this.spinnerService.show();
    this.largeShareholderService.createLargeShareholder(request).pipe(
      switchMap(created => {
        const connectionRequest: CreateConnectionRequest = {
          largeShareholderId: created.id,
          directorId: item.entityType === EntityType.Director ? item.id : undefined,
          companyId: item.entityType === EntityType.Company ? item.id : undefined,
          opbCompanyId: item.entityType === EntityType.OpbCompany ? item.id : undefined
        };
        return this.largeShareholderService.createConnection(connectionRequest).pipe(
          map(() => created)
        );
      })
    ).subscribe({
      next: (created) => {
        this.spinnerService.hide();
        this.addShareholderToOwnership({
          id: created.id,
          name: created.name,
          entityType: EntityType.LargeShareholder,
          categoryName: created.categoryName,
          subCategoryName: created.subCategoryName
        });
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to create large shareholder');
        console.error('Create LSH error:', err);
      }
    });
  }

  onDeleteShareholder(row: OwnershipLargeShareholderDto): void {
    if (!this.companyId) return;

    this.confirmationDialog.openConfirmationDialog(
      'Delete Shareholder',
      `Are you sure you want to remove "${row.largeShareholderName}" from the ownership list?`,
      'Delete',
      'Cancel',
      'delete',
      'close'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.removeShareholders([row.id]);
      }
    });
  }

  get selectedShareholders(): OwnershipLargeShareholderDto[] {
    return this.shareholderDataSource.data.filter((row: any) => row.highlighted);
  }

  onDeleteSelectedShareholders(): void {
    if (!this.companyId) return;

    const selected = this.selectedShareholders;
    if (selected.length === 0) return;

    const names = selected.map(s => `"${s.largeShareholderName}"`);
    const namesList = names.length <= 5
      ? names.join(', ')
      : `${names.slice(0, 5).join(', ')} and ${names.length - 5} more`;
    const message = `Are you sure you want to remove ${selected.length} ${selected.length === 1 ? 'shareholder' : 'shareholders'} (${namesList}) from the ownership list?`;

    this.confirmationDialog.openConfirmationDialog(
      'Delete Selected Shareholders',
      message,
      'Delete',
      'Cancel',
      'delete',
      'close'
    ).subscribe(confirmed => {
      if (confirmed) {
        this.removeShareholders(selected.map(s => s.id));
      }
    });
  }

  private removeShareholders(rowIds: string[]): void {
    if (rowIds.length === 0) return;
    const idSet = new Set(rowIds);

    // Track for deletion on save, keyed by the row's surrogate id so one of several duplicate
    // rows can be removed while the others stay. Newly-added rows just disappear; carried-forward
    // rows have no row for this year yet (their id points at the source year), so removing them
    // is UI-only — they simply won't be materialized for this year on save.
    rowIds.forEach(id => {
      if (this.newRowIds.includes(id)) {
        this.newRowIds = this.newRowIds.filter(x => x !== id);
      } else if (!this.isLargeShareholdersCarriedForward) {
        this.deletedOwnershipRowIds.push(id);
      }
    });

    // Remove from data source
    this.shareholderDataSource.data = this.shareholderDataSource.data.filter(
      s => !idSet.has(s.id)
    );

    // Remove from form array (iterate in reverse to keep indices valid)
    for (let i = this.shareholderRowsArray.length - 1; i >= 0; i--) {
      const id = this.shareholderRowsArray.at(i).get('id')?.value;
      if (idSet.has(id)) {
        this.shareholderRowsArray.removeAt(i);
      }
    }

    this.form.markAsDirty();
  }

  formatOther(value: number | undefined | null): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatOtherInt(value: number | undefined | null): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE');
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.ownershipService.getOwnership(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        if (resource.state === 'present' && resource.data) {
          this.otherData = {
            ...resource.data,
            boardMembers: resource.context?.boardMembers ?? [],
            executiveMembers: resource.context?.executiveMembers ?? []
          };
        } else {
          this.otherData = null;
        }
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private subscribeToShareChanges(): void {
    const shareFields = ['firstClassTotalShares', 'secondClassTotalShares', 'thirdClassTotalShares'];
    const coefficientFields = ['firstClassStockCoefficientId', 'secondClassStockCoefficientId', 'thirdClassStockCoefficientId'];
    const allChanges$ = [...shareFields, ...coefficientFields].map(f => this.form.get(f)!.valueChanges);

    merge(...allChanges$).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.recalculateCapitalSharePercentages();
      this.recalculateVotingRights();
      // Editing a class total re-derives every row's percentage off it. Those rows must be marked
      // dirty or the new percentages are shown but never saved: performSave only sends dirty rows,
      // so a total entered after the member rows were saved left their stored percentage null.
      // Only a real edit counts -- a load patches these same controls, and dirtying the form there
      // would light up the Save button and the unsaved-changes guard on every open.
      const isUserEdit = !this.isPopulating;
      this.recalculateRowSharePercentages(isUserEdit);
      this.recalculateRowVotingRights(isUserEdit);
      this.updateStockClassErrors(false);
    });

    // Category/Type don't affect calculations, but they're part of the required Stock Classes
    // configuration — re-evaluate the error state live so highlights clear as they're filled.
    const configFields = [
      'secondClassStockCategoryId', 'secondClassStockType',
      'thirdClassStockCategoryId', 'thirdClassStockType',
    ];
    merge(...configFields.map(f => this.form.get(f)!.valueChanges))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateStockClassErrors(false));
  }

  /** Subscribe to individual row controls for bidirectional shares <-> percentage calculation */
  private subscribeToRowControls(): void {
    this.rowChanges$.next(); // clean up previous row subscriptions

    const classes = [
      { shares: 'firstClassShares', pct: 'firstClassSharesPercentage', totalField: 'firstClassTotalShares' },
      { shares: 'secondClassShares', pct: 'secondClassSharesPercentage', totalField: 'secondClassTotalShares' },
      { shares: 'thirdClassShares', pct: 'thirdClassSharesPercentage', totalField: 'thirdClassTotalShares' },
    ];

    const arrayNames = ['boardRows', 'managementRows', 'executiveRows', 'shareholderRows'];

    arrayNames.forEach(arrayName => {
      const formArray = this.form.get(arrayName) as FormArray;
      if (!formArray) return;
      formArray.controls.forEach(ctrl => {
        const row = ctrl as FormGroup;
        classes.forEach(cls => {
          // shares changed → recalculate percentage
          row.get(cls.shares)!.valueChanges.pipe(
            takeUntil(this.rowChanges$), takeUntil(this.destroy$)
          ).subscribe(shares => {
            const total = this.form.get(cls.totalField)?.value || 0;
            const pct = total ? ((shares || 0) / total) * 100 : null;
            row.get(cls.pct)!.setValue(pct, { emitEvent: false });
            this.recalculateRowVotingRights();
          });

          // percentage changed → recalculate shares
          row.get(cls.pct)!.valueChanges.pipe(
            takeUntil(this.rowChanges$), takeUntil(this.destroy$)
          ).subscribe(pct => {
            const total = this.form.get(cls.totalField)?.value || 0;
            const shares = (pct != null && total) ? Math.round((pct / 100) * total) : null;
            row.get(cls.shares)!.setValue(shares, { emitEvent: false });
            this.recalculateRowVotingRights();
          });
        });
      });
    });
  }

  private recalculateCapitalSharePercentages(): void {
    const first = this.form.get('firstClassTotalShares')?.value || 0;
    const second = this.form.get('secondClassTotalShares')?.value || 0;
    const third = this.form.get('thirdClassTotalShares')?.value || 0;
    const total = first + second + third;

    const pct = (val: number) => total ? (val / total) * 100 : null;

    this.form.get('firstClassCapitalSharePercentage')?.setValue(pct(first), { emitEvent: false });
    this.form.get('secondClassCapitalSharePercentage')?.setValue(pct(second), { emitEvent: false });
    this.form.get('thirdClassCapitalSharePercentage')?.setValue(pct(third), { emitEvent: false });
  }

  private getCoefficient(coefficientId: string | null): number {
    if (!coefficientId) return 1;
    const coeff = this.stockCoefficients.find(c => c.id === coefficientId);
    return coeff?.value ?? 1;
  }

  private recalculateVotingRights(): void {
    const classes = [
      { shares: 'firstClassTotalShares', coeff: 'firstClassStockCoefficientId', vr: 'firstClassTotalVotingRights', vrPct: 'firstClassTotalVotingRightsPercentage' },
      { shares: 'secondClassTotalShares', coeff: 'secondClassStockCoefficientId', vr: 'secondClassTotalVotingRights', vrPct: 'secondClassTotalVotingRightsPercentage' },
      { shares: 'thirdClassTotalShares', coeff: 'thirdClassStockCoefficientId', vr: 'thirdClassTotalVotingRights', vrPct: 'thirdClassTotalVotingRightsPercentage' },
    ];

    const votingRights = classes.map(c => {
      const shares = this.form.get(c.shares)?.value || 0;
      const coefficient = this.getCoefficient(this.form.get(c.coeff)?.value);
      return shares * coefficient;
    });

    classes.forEach((c, i) => {
      this.form.get(c.vr)?.setValue(Math.round(votingRights[i]), { emitEvent: false });
    });

    const total = votingRights.reduce((a, b) => a + b, 0);
    const pct = (val: number) => total ? (val / total) * 100 : null;

    classes.forEach((c, i) => {
      this.form.get(c.vrPct)?.setValue(pct(votingRights[i]), { emitEvent: false });
    });
  }

  /**
   * Re-derives each row's share percentages from the class totals.
   * @param markDirty marks a row dirty when this actually changes one of its values, so the new
   * percentage reaches the server — performSave only sends dirty rows. Pass false while loading.
   */
  private recalculateRowSharePercentages(markDirty = false): void {
    const classes = [
      { shares: 'firstClassShares', pct: 'firstClassSharesPercentage', total: this.form.get('firstClassTotalShares')?.value || 0 },
      { shares: 'secondClassShares', pct: 'secondClassSharesPercentage', total: this.form.get('secondClassTotalShares')?.value || 0 },
      { shares: 'thirdClassShares', pct: 'thirdClassSharesPercentage', total: this.form.get('thirdClassTotalShares')?.value || 0 },
    ];

    const arrayNames = ['boardRows', 'managementRows', 'executiveRows', 'shareholderRows'];

    arrayNames.forEach(arrayName => {
      const formArray = this.form.get(arrayName) as FormArray;
      if (!formArray) return;
      formArray.controls.forEach(c => {
        const row = c as FormGroup;
        classes.forEach(cls => {
          // Distinguish an explicit 0 (member holds none of this class — must stay 0) from an
          // empty cell (null). `|| 0` would collapse the two and wipe a saved 0% back to blank.
          const shares = row.get(cls.shares)?.value;
          const pct = row.get(cls.pct)?.value;
          if (shares != null) {
            // Absolute shares (including an explicit 0) are the source of truth → derive the %.
            this.setRowValue(row, cls.pct, cls.total ? (shares / cls.total) * 100 : (shares === 0 ? 0 : null), markDirty);
          } else if (pct != null) {
            // A percentage was entered before the Capital Share total existed (shares still
            // empty). Preserve it and derive the shares instead of zeroing the percentage.
            this.setRowValue(row, cls.shares, cls.total ? Math.round((pct / 100) * cls.total) : (pct === 0 ? 0 : null), markDirty);
          } else {
            this.setRowValue(row, cls.pct, null, markDirty);
          }
        });
      });
    });
  }

  /**
   * Writes a derived value into a row, marking the row dirty only when the value actually moved —
   * a recalculation that changes nothing must not enable the Save button.
   */
  private setRowValue(row: FormGroup, controlName: string, value: number | null, markDirty: boolean): void {
    const control = row.get(controlName);
    if (!control || control.value === value) return;

    control.setValue(value, { emitEvent: false });
    if (markDirty) {
      row.markAsDirty();
    }
  }

  /**
   * Re-derives each row's voting rights from its shares and the class coefficients.
   * @param markDirty see <see cref="recalculateRowSharePercentages"/> — same reason.
   */
  private recalculateRowVotingRights(markDirty = false): void {
    const firstCoeff = this.getCoefficient(this.form.get('firstClassStockCoefficientId')?.value);
    const secondCoeff = this.getCoefficient(this.form.get('secondClassStockCoefficientId')?.value);
    const thirdCoeff = this.getCoefficient(this.form.get('thirdClassStockCoefficientId')?.value);

    const arrayNames = ['boardRows', 'managementRows', 'executiveRows', 'shareholderRows'];
    const allRows: FormGroup[] = [];

    arrayNames.forEach(arrayName => {
      const formArray = this.form.get(arrayName) as FormArray;
      if (formArray) {
        formArray.controls.forEach(c => allRows.push(c as FormGroup));
      }
    });

    // Calculate voting rights for each row
    allRows.forEach(row => {
      const vr = (row.get('firstClassShares')?.value || 0) * firstCoeff
        + (row.get('secondClassShares')?.value || 0) * secondCoeff
        + (row.get('thirdClassShares')?.value || 0) * thirdCoeff;
      this.setRowValue(row, 'votingRights', Math.round(vr), markDirty);
    });

    const totalVr = (this.form.get('firstClassTotalVotingRights')?.value || 0)
      + (this.form.get('secondClassTotalVotingRights')?.value || 0)
      + (this.form.get('thirdClassTotalVotingRights')?.value || 0);

    allRows.forEach(row => {
      const vr = row.get('votingRights')?.value || 0;
      this.setRowValue(row, 'votingRightPercentage', totalVr ? (vr / totalVr) * 100 : null, markDirty);
    });
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'ownership', year }));
    this.loadData();
  }

  private loadData(): void {
    if (!this.companyId) return;

    this.deletedOwnershipRowIds = [];
    this.newRowIds = [];
    this.spinnerService.show();
    this.ownershipService.getOwnership(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const response = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isOwnershipDetailsCarriedForward = resource.state === 'carried-forward';
        this.isStockClassConfigCarriedForward = response?.isStockClassConfigCarriedForward ?? false;
        this.isLargeShareholdersCarriedForward = response?.isLargeShareholdersCarriedForward ?? false;
        this.showYearWarning = this.isOwnershipDetailsCarriedForward || resource.state === 'absent';

        // Members come from context — populated regardless of envelope state.
        const boardMembers = resource.context?.boardMembers ?? [];
        const executiveMembers = resource.context?.executiveMembers ?? [];
        const boardType = resource.context?.boardType ?? 0;
        // Patching the class totals below re-fires the recalculation subscriptions. Flag the load
        // so they don't read as a user edit and dirty every row.
        this.isPopulating = true;
        try {
          this.populateMemberTables(boardMembers, executiveMembers, boardType);

          if (response) {
            this.populateOwnershipFields(response);
          } else {
            this.clearOwnershipFields();
          }
        } finally {
          this.isPopulating = false;
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading ownership data');
        }

        console.error('Ownership data error:', error);
      }
    });
  }

  private populateMemberTables(
    boardMembers: OwnershipBoardMemberDto[],
    executiveMembers: OwnershipExecutiveMemberDto[],
    boardType: number
  ): void {
    this.companyBoardType = boardType;

    if (boardType !== 0) {
      const supervisory = boardMembers.filter(m => m.boardType === 1);
      const management = boardMembers.filter(m => m.boardType === 2);
      this.boardDataSource.data = supervisory;
      this.managementDataSource.data = management;
      this.buildBoardFormArrays(supervisory, management);
    } else {
      this.boardDataSource.data = boardMembers;
      this.managementDataSource.data = [];
      this.buildBoardFormArrays(boardMembers, []);
    }

    this.executiveDataSource.data = executiveMembers;
    this.form.setControl('executiveRows', this.fb.array(
      executiveMembers.map(m => this.createExecutiveRowGroup(m))
    ));

    this.onBoardSort(this.currentSort(this.boardSort));
    if (boardType !== 0) {
      this.onManagementSort(this.currentSort(this.managementSort));
    }
    this.onExecutiveSort(this.currentSort(this.executiveSort));
  }

  private populateOwnershipFields(response: OwnershipResponseDto): void {
    this.form.patchValue({
      firstClassStockCoefficientId: response.firstClassStockCoefficientId ?? null,
      firstClassStockCategoryId: response.firstClassStockCategoryId ?? null,
      firstClassStockType: this.normalizeStockType(response.firstClassStockType),
      firstClassTotalShares: response.firstClassTotalShares ?? null,
      firstClassCapitalSharePercentage: response.firstClassCapitalSharePercentage ?? null,
      firstClassTotalVotingRightsPercentage: response.firstClassTotalVotingRightsPercentage ?? null,
      firstClassTotalVotingRights: response.firstClassTotalVotingRights ?? null,
      secondClassStockCoefficientId: response.secondClassStockCoefficientId ?? null,
      secondClassStockCategoryId: response.secondClassStockCategoryId ?? null,
      secondClassStockType: this.normalizeStockType(response.secondClassStockType),
      secondClassTotalShares: response.secondClassTotalShares ?? null,
      secondClassCapitalSharePercentage: response.secondClassCapitalSharePercentage ?? null,
      secondClassTotalVotingRightsPercentage: response.secondClassTotalVotingRightsPercentage ?? null,
      secondClassTotalVotingRights: response.secondClassTotalVotingRights ?? null,
      thirdClassStockCoefficientId: response.thirdClassStockCoefficientId ?? null,
      thirdClassStockCategoryId: response.thirdClassStockCategoryId ?? null,
      thirdClassStockType: this.normalizeStockType(response.thirdClassStockType),
      thirdClassTotalShares: response.thirdClassTotalShares ?? null,
      thirdClassCapitalSharePercentage: response.thirdClassCapitalSharePercentage ?? null,
      thirdClassTotalVotingRightsPercentage: response.thirdClassTotalVotingRightsPercentage ?? null,
      thirdClassTotalVotingRights: response.thirdClassTotalVotingRights ?? null,
      comments: response.comments ?? ''
    });

    const largeShareholders = response.largeShareholders ?? [];
    this.shareholderDataSource.data = largeShareholders;
    this.form.setControl('shareholderRows', this.fb.array(
      largeShareholders.map(m => this.createShareholderRowGroup(m))
    ));
    this.onShareholderSort(this.currentSort(this.shareholderSort));

    this.recalculateCapitalSharePercentages();
    this.recalculateVotingRights();
    this.recalculateRowSharePercentages();
    this.recalculateRowVotingRights();
    this.subscribeToRowControls();
    this.form.markAsPristine();
  }

  private clearOwnershipFields(): void {
    this.form.patchValue({
      firstClassStockCoefficientId: null,
      firstClassStockCategoryId: null,
      firstClassStockType: '',
      firstClassTotalShares: null,
      firstClassCapitalSharePercentage: null,
      firstClassTotalVotingRightsPercentage: null,
      firstClassTotalVotingRights: null,
      secondClassStockCoefficientId: null,
      secondClassStockCategoryId: null,
      secondClassStockType: '',
      secondClassTotalShares: null,
      secondClassCapitalSharePercentage: null,
      secondClassTotalVotingRightsPercentage: null,
      secondClassTotalVotingRights: null,
      thirdClassStockCoefficientId: null,
      thirdClassStockCategoryId: null,
      thirdClassStockType: '',
      thirdClassTotalShares: null,
      thirdClassCapitalSharePercentage: null,
      thirdClassTotalVotingRightsPercentage: null,
      thirdClassTotalVotingRights: null,
      comments: ''
    });
    this.shareholderDataSource.data = [];
    this.form.setControl('shareholderRows', this.fb.array([]));
    this.form.markAsPristine();
  }

  private buildBoardFormArrays(boardRows: OwnershipBoardMemberDto[], managementRows: OwnershipBoardMemberDto[]): void {
    this.form.setControl('boardRows', this.fb.array(
      boardRows.map(m => this.createBoardRowGroup(m))
    ));
    this.form.setControl('managementRows', this.fb.array(
      managementRows.map(m => this.createBoardRowGroup(m))
    ));
  }

  private createBoardRowGroup(member: OwnershipBoardMemberDto): FormGroup {
    return this.fb.group({
      detailsId: [member.detailsId],
      boardMemberId: [member.boardMemberId],
      directorId: [member.directorId],
      firstClassSharesPercentage: [member.firstClassSharesPercentage ?? null],
      firstClassShares: [member.firstClassShares ?? null],
      secondClassSharesPercentage: [member.secondClassSharesPercentage ?? null],
      secondClassShares: [member.secondClassShares ?? null],
      thirdClassSharesPercentage: [member.thirdClassSharesPercentage ?? null],
      thirdClassShares: [member.thirdClassShares ?? null],
      votingRightPercentage: [member.votingRightPercentage ?? null],
      votingRights: [member.votingRights ?? null]
    });
  }

  private createExecutiveRowGroup(member: OwnershipExecutiveMemberDto): FormGroup {
    return this.fb.group({
      detailsId: [member.detailsId],
      executiveMemberId: [member.executiveMemberId],
      directorId: [member.directorId],
      firstClassSharesPercentage: [member.firstClassSharesPercentage ?? null],
      firstClassShares: [member.firstClassShares ?? null],
      secondClassSharesPercentage: [member.secondClassSharesPercentage ?? null],
      secondClassShares: [member.secondClassShares ?? null],
      thirdClassSharesPercentage: [member.thirdClassSharesPercentage ?? null],
      thirdClassShares: [member.thirdClassShares ?? null],
      votingRightPercentage: [member.votingRightPercentage ?? null],
      votingRights: [member.votingRights ?? null]
    });
  }

  private createShareholderRowGroup(member: OwnershipLargeShareholderDto): FormGroup {
    return this.fb.group({
      id: [member.id ?? null],
      largeShareholderId: [member.largeShareholderId],
      entityType: [member.entityType ?? EntityType.LargeShareholder],
      firstClassSharesPercentage: [member.firstClassSharesPercentage ?? null],
      firstClassShares: [member.firstClassShares ?? null],
      secondClassSharesPercentage: [member.secondClassSharesPercentage ?? null],
      secondClassShares: [member.secondClassShares ?? null],
      thirdClassSharesPercentage: [member.thirdClassSharesPercentage ?? null],
      thirdClassShares: [member.thirdClassShares ?? null],
      votingRightPercentage: [member.votingRightPercentage ?? null],
      votingRights: [member.votingRights ?? null]
    });
  }

  private sortBoardTable(sort: Sort, dataSource: MatTableDataSource<OwnershipBoardMemberDto>, formArray: FormArray, formArrayName: string): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const paired = dataSource.data.map((row, i) => {
      const group = formArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((a, b) => {
      let valA: any;
      let valB: any;

      if (sort.active === 'name') {
        valA = a.merged.directorFullName ?? '';
        valB = b.merged.directorFullName ?? '';
        return dir * valA.localeCompare(valB);
      }

      // All other columns are numeric
      valA = a.merged[sort.active] ?? -Infinity;
      valB = b.merged[sort.active] ?? -Infinity;
      return dir * ((Number(valA) || 0) - (Number(valB) || 0));
    });

    dataSource.data = paired.map(p => p.merged);
    // Reuse existing FormGroup instances so per-row dirty state, values, and
    // valueChanges subscriptions survive the reorder.
    this.form.setControl(formArrayName, this.fb.array(paired.map(p => p.group)));
  }

  private sortExecutiveTable(sort: Sort): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const paired = this.executiveDataSource.data.map((row, i) => {
      const group = this.executiveRowsArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((a, b) => {
      let valA: any;
      let valB: any;

      if (sort.active === 'name') {
        valA = a.merged.directorFullName ?? '';
        valB = b.merged.directorFullName ?? '';
        return dir * valA.localeCompare(valB);
      }

      valA = a.merged[sort.active] ?? -Infinity;
      valB = b.merged[sort.active] ?? -Infinity;
      return dir * ((Number(valA) || 0) - (Number(valB) || 0));
    });

    this.executiveDataSource.data = paired.map(p => p.merged);
    this.form.setControl('executiveRows', this.fb.array(paired.map(p => p.group)));
  }

  private sortShareholderTable(sort: Sort): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;
    const paired = this.shareholderDataSource.data.map((row, i) => {
      const group = this.shareholderRowsArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.value }, group };
    });

    paired.sort((a, b) => {
      let valA: any;
      let valB: any;

      switch (sort.active) {
        case 'name':
          valA = a.merged.largeShareholderName ?? '';
          valB = b.merged.largeShareholderName ?? '';
          return dir * valA.localeCompare(valB);
        case 'category':
          valA = a.merged.categoryName ?? '';
          valB = b.merged.categoryName ?? '';
          return dir * valA.localeCompare(valB);
        case 'subCategory':
          valA = a.merged.subCategoryName ?? '';
          valB = b.merged.subCategoryName ?? '';
          return dir * valA.localeCompare(valB);
        default:
          valA = a.merged[sort.active] ?? -Infinity;
          valB = b.merged[sort.active] ?? -Infinity;
          return dir * ((Number(valA) || 0) - (Number(valB) || 0));
      }
    });

    this.shareholderDataSource.data = paired.map(p => p.merged);
    this.form.setControl('shareholderRows', this.fb.array(paired.map(p => p.group)));
  }

  private resetForm(): void {
    this.rowChanges$.next();
    this.boardDataSource.data = [];
    this.managementDataSource.data = [];
    this.executiveDataSource.data = [];
    this.shareholderDataSource.data = [];
    this.form.setControl('boardRows', this.fb.array([]));
    this.form.setControl('managementRows', this.fb.array([]));
    this.form.setControl('executiveRows', this.fb.array([]));
    this.form.setControl('shareholderRows', this.fb.array([]));
    this.form.patchValue({
      firstClassStockCoefficientId: null,
      firstClassStockCategoryId: null,
      firstClassStockType: '',
      firstClassTotalShares: null,
      firstClassCapitalSharePercentage: null,
      firstClassTotalVotingRightsPercentage: null,
      firstClassTotalVotingRights: null,
      secondClassStockCoefficientId: null,
      secondClassStockCategoryId: null,
      secondClassStockType: '',
      secondClassTotalShares: null,
      secondClassCapitalSharePercentage: null,
      secondClassTotalVotingRightsPercentage: null,
      secondClassTotalVotingRights: null,
      thirdClassStockCoefficientId: null,
      thirdClassStockCategoryId: null,
      thirdClassStockType: '',
      thirdClassTotalShares: null,
      thirdClassCapitalSharePercentage: null,
      thirdClassTotalVotingRightsPercentage: null,
      thirdClassTotalVotingRights: null,
      comments: ''
    });
    this.form.markAsPristine();
  }

  /**
   * Stock-class safety check: when a 2nd or 3rd share class has Capital shares entered, its
   * full Stock Classes configuration (Coefficient, Category and Type) must be set. The
   * coefficient feeds the voting-rights calculation (shares × coefficient — see
   * recalculateVotingRights / recalculateRowVotingRights) and a missing one silently defaults
   * to ×1 (getCoefficient), producing wrong voting rights. The 1st class is exempt — common
   * shares legitimately default to ×1.
   *
   * Flags each missing Stock Classes field with a `required` error so the shared
   * `.ht-input.ng-invalid.ng-touched` style highlights it; clears the error once filled.
   * Returns true when the configuration is invalid.
   */
  private updateStockClassErrors(markTouched: boolean): boolean {
    const classes = [
      { shares: 'secondClassTotalShares', fields: ['secondClassStockCoefficientId', 'secondClassStockCategoryId', 'secondClassStockType'] },
      { shares: 'thirdClassTotalShares', fields: ['thirdClassStockCoefficientId', 'thirdClassStockCategoryId', 'thirdClassStockType'] },
    ];
    let invalid = false;
    classes.forEach(c => {
      const hasShares = (this.form.get(c.shares)?.value || 0) > 0;
      c.fields.forEach(field => {
        const ctrl = this.form.get(field)!;
        if (hasShares && !ctrl.value) {
          ctrl.setErrors({ required: true });
          if (markTouched) ctrl.markAsTouched();
          invalid = true;
        } else if (ctrl.hasError('required')) {
          ctrl.setErrors(null);
        }
      });
    });
    return invalid;
  }

  /**
   * Zero-fill the share columns for Directors (Board/Supervisory/Management) and Officers
   * (Executive) who hold no shares of a given class. For each share class the company actually
   * has (TotalShares > 0), a member row whose Shares AND % cells are both empty is treated as
   * "holds none of this class" and both are set to 0; voting rights of a no-holding member are
   * likewise zeroed. Rows touched here are marked dirty so the normal save collection includes
   * them. Classes the company doesn't use, and cells already holding a value, are left untouched.
   */
  private zeroFillEmptyMemberShares(): void {
    const activeClasses = [
      { shares: 'firstClassShares', pct: 'firstClassSharesPercentage', total: 'firstClassTotalShares' },
      { shares: 'secondClassShares', pct: 'secondClassSharesPercentage', total: 'secondClassTotalShares' },
      { shares: 'thirdClassShares', pct: 'thirdClassSharesPercentage', total: 'thirdClassTotalShares' },
    ].filter(c => (this.form.get(c.total)?.value || 0) > 0);

    if (activeClasses.length === 0) return;

    const isEmpty = (val: any) => val == null || val === '';

    [this.boardRowsArray, this.managementRowsArray, this.executiveRowsArray].forEach(arr => {
      arr.controls.forEach(group => {
        let rowChanged = false;

        activeClasses.forEach(c => {
          const sharesCtrl = group.get(c.shares)!;
          const pctCtrl = group.get(c.pct)!;
          if (isEmpty(sharesCtrl.value) && isEmpty(pctCtrl.value)) {
            sharesCtrl.setValue(0, { emitEvent: false });
            pctCtrl.setValue(0, { emitEvent: false });
            rowChanged = true;
          }
        });

        // A member holding no shares has no voting rights — fill the (empty) calculated cells.
        const vrCtrl = group.get('votingRights')!;
        const vrPctCtrl = group.get('votingRightPercentage')!;
        if (isEmpty(vrCtrl.value)) { vrCtrl.setValue(0, { emitEvent: false }); rowChanged = true; }
        if (isEmpty(vrPctCtrl.value)) { vrPctCtrl.setValue(0, { emitEvent: false }); rowChanged = true; }

        if (rowChanged) group.markAsDirty();
      });
    });
  }

  private performSave(): void {
    if (!this.companyId) return;

    // Materialize 0 for Directors/Officers with no shares before collecting dirty rows.
    this.zeroFillEmptyMemberShares();

    const formVal = this.form.value;

    // Collect dirty board members
    const boardMembers: OwnershipBoardMemberUpsertDto[] = [];
    this.boardRowsArray.controls.forEach(group => {
      if (group.dirty) {
        boardMembers.push(this.buildBoardMemberUpsertDto(group.value));
      }
    });
    this.managementRowsArray.controls.forEach(group => {
      if (group.dirty) {
        boardMembers.push(this.buildBoardMemberUpsertDto(group.value));
      }
    });

    // Collect dirty executive members
    const executiveMembers: OwnershipExecutiveMemberUpsertDto[] = [];
    this.executiveRowsArray.controls.forEach(group => {
      if (group.dirty) {
        executiveMembers.push(this.buildExecutiveMemberUpsertDto(group.value));
      }
    });

    // Collect dirty or newly added large shareholders.
    // When carry-forward, also send untouched rows zeroed-out so every visible
    // shareholder is materialized for this year (otherwise they vanish on next load).
    const largeShareholders: OwnershipLargeShareholderUpsertDto[] = [];
    this.shareholderRowsArray.controls.forEach(group => {
      const rowId = group.get('id')?.value;
      const isNew = this.newRowIds.includes(rowId);
      // Carried-forward rows are prior-year templates with no row for this year yet, so they
      // must be inserted (id = null) rather than update the source-year row they came from.
      const insertAsNew = isNew || this.isLargeShareholdersCarriedForward;
      if (group.dirty || isNew) {
        largeShareholders.push(this.buildShareholderUpsertDto(group.value, insertAsNew));
      } else if (this.isLargeShareholdersCarriedForward) {
        largeShareholders.push(this.buildZeroShareholderUpsertDto(group.value));
      }
    });

    // Check top-level fields
    const topLevelFields = [
      'firstClassStockCoefficientId', 'firstClassStockCategoryId', 'firstClassStockType', 'firstClassTotalShares',
      'firstClassCapitalSharePercentage', 'firstClassTotalVotingRightsPercentage', 'firstClassTotalVotingRights',
      'secondClassStockCoefficientId', 'secondClassStockCategoryId', 'secondClassStockType', 'secondClassTotalShares',
      'secondClassCapitalSharePercentage', 'secondClassTotalVotingRightsPercentage', 'secondClassTotalVotingRights',
      'thirdClassStockCoefficientId', 'thirdClassStockCategoryId', 'thirdClassStockType', 'thirdClassTotalShares',
      'thirdClassCapitalSharePercentage', 'thirdClassTotalVotingRightsPercentage', 'thirdClassTotalVotingRights',
      'comments'
    ];
    const topLevelDirty = this.isStockClassConfigCarriedForward || topLevelFields.some(field => this.form.get(field)?.dirty);

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, [
      { formField: 'boardShares', type: KeywordType.OwnershipBoardShares },
      { formField: 'largestShareholder', type: KeywordType.OwnershipLargestShareholder },
      { formField: 'totalShares', type: KeywordType.OwnershipTotalShares }
    ], this.keywords);

    const hasOwnershipData = boardMembers.length > 0 || executiveMembers.length > 0 || largeShareholders.length > 0 || this.deletedOwnershipRowIds.length > 0 || this.newRowIds.length > 0 || topLevelDirty;
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.OwnershipBoardShares || k.type === KeywordType.OwnershipLargestShareholder || k.type === KeywordType.OwnershipTotalShares
    );

    if (!hasOwnershipData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();

    const request: OwnershipUpsertRequest = {
      year: parseInt(this.selectedYear),
      firstClassStockCoefficientId: formVal.firstClassStockCoefficientId,
      firstClassStockCategoryId: formVal.firstClassStockCategoryId,
      firstClassStockType: formVal.firstClassStockType,
      firstClassTotalShares: formVal.firstClassTotalShares,
      firstClassCapitalSharePercentage: formVal.firstClassCapitalSharePercentage,
      firstClassTotalVotingRightsPercentage: formVal.firstClassTotalVotingRightsPercentage,
      firstClassTotalVotingRights: formVal.firstClassTotalVotingRights,
      secondClassStockCoefficientId: formVal.secondClassStockCoefficientId,
      secondClassStockCategoryId: formVal.secondClassStockCategoryId,
      secondClassStockType: formVal.secondClassStockType,
      secondClassTotalShares: formVal.secondClassTotalShares,
      secondClassCapitalSharePercentage: formVal.secondClassCapitalSharePercentage,
      secondClassTotalVotingRightsPercentage: formVal.secondClassTotalVotingRightsPercentage,
      secondClassTotalVotingRights: formVal.secondClassTotalVotingRights,
      thirdClassStockCoefficientId: formVal.thirdClassStockCoefficientId,
      thirdClassStockCategoryId: formVal.thirdClassStockCategoryId,
      thirdClassStockType: formVal.thirdClassStockType,
      thirdClassTotalShares: formVal.thirdClassTotalShares,
      thirdClassCapitalSharePercentage: formVal.thirdClassCapitalSharePercentage,
      thirdClassTotalVotingRightsPercentage: formVal.thirdClassTotalVotingRightsPercentage,
      thirdClassTotalVotingRights: formVal.thirdClassTotalVotingRights,
      comments: formVal.comments,
      boardMembers,
      executiveMembers,
      largeShareholders,
      deletedOwnershipRowIds: this.deletedOwnershipRowIds
    };

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasOwnershipData
      ? this.ownershipService.updateOwnership(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([ownershipResult, keywordsResult]) => {
        if (hasOwnershipData) {
          this.showYearWarning = false;
          this.deletedOwnershipRowIds = [];
          this.newRowIds = [];
        }
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
        // The server refuses rows for members the year's roster no longer holds — someone was
        // removed on the Board tab while these tables were still showing the old roster.
        if (membersWereDropped(ownershipResult)) {
          this.toast.warning(MEMBERS_DROPPED_MESSAGE);
        }
        // Likewise for shareholders that were merged away or deleted on the Large Shareholders
        // screen — often in another browser tab — after this table was loaded.
        if (shareholdersWereDropped(ownershipResult)) {
          this.toast.warning(SHAREHOLDERS_DROPPED_MESSAGE);
        }
        if (hasOwnershipData) {
          this.loadData();
        }
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private buildBoardMemberUpsertDto(val: any): OwnershipBoardMemberUpsertDto {
    return {
      detailsId: val.detailsId || undefined,
      boardMemberId: val.boardMemberId,
      directorId: val.directorId,
      firstClassShares: val.firstClassShares,
      firstClassSharesPercentage: val.firstClassSharesPercentage,
      secondClassShares: val.secondClassShares,
      secondClassSharesPercentage: val.secondClassSharesPercentage,
      thirdClassShares: val.thirdClassShares,
      thirdClassSharesPercentage: val.thirdClassSharesPercentage,
      votingRights: val.votingRights,
      votingRightPercentage: val.votingRightPercentage
    };
  }

  private buildExecutiveMemberUpsertDto(val: any): OwnershipExecutiveMemberUpsertDto {
    return {
      detailsId: val.detailsId || undefined,
      executiveMemberId: val.executiveMemberId,
      directorId: val.directorId,
      firstClassShares: val.firstClassShares,
      firstClassSharesPercentage: val.firstClassSharesPercentage,
      secondClassShares: val.secondClassShares,
      secondClassSharesPercentage: val.secondClassSharesPercentage,
      thirdClassShares: val.thirdClassShares,
      thirdClassSharesPercentage: val.thirdClassSharesPercentage,
      votingRights: val.votingRights,
      votingRightPercentage: val.votingRightPercentage
    };
  }

  private buildShareholderUpsertDto(val: any, insertAsNew: boolean): OwnershipLargeShareholderUpsertDto {
    return {
      id: insertAsNew ? null : val.id,
      largeShareholderId: val.largeShareholderId,
      entityType: val.entityType,
      firstClassShares: val.firstClassShares,
      firstClassSharesPercentage: val.firstClassSharesPercentage,
      secondClassShares: val.secondClassShares,
      secondClassSharesPercentage: val.secondClassSharesPercentage,
      thirdClassShares: val.thirdClassShares,
      thirdClassSharesPercentage: val.thirdClassSharesPercentage,
      votingRights: val.votingRights,
      votingRightPercentage: val.votingRightPercentage
    };
  }

  private buildZeroShareholderUpsertDto(val: any): OwnershipLargeShareholderUpsertDto {
    // Only reached for carried-forward rows, which have no row for this year yet → insert (id null).
    return {
      id: null,
      largeShareholderId: val.largeShareholderId,
      entityType: val.entityType,
      firstClassShares: 0,
      firstClassSharesPercentage: 0,
      secondClassShares: 0,
      secondClassSharesPercentage: 0,
      thirdClassShares: 0,
      thirdClassSharesPercentage: 0,
      votingRights: 0,
      votingRightPercentage: 0
    };
  }
}
